import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bot, ChevronRight, MessageSquare, Sparkles } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Container } from "@/components/layout/Container";
import { PageHeader } from "@/components/content/PageHeader";
import { EmptyState } from "@/components/content/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { getSubjectIcon } from "@/components/content/SubjectIcon";
import { StaggerList, StaggerItem } from "@/components/motion/Stagger";
import { useDashboard } from "@/hooks/useProgress";
import { useChapters, useSubjects } from "@/hooks/useCurriculum";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAITutorStore } from "@/stores/ai-tutor-store";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import { cn } from "@/lib/cn";
import type { ApiEnvelope, ChatMessage, UserProgress } from "@/lib/api-types";

/**
 * Cross-chapter AI Tutor history. Phase 8g.
 *
 * Deploy-free fan-out: the backend has no "all my conversations" endpoint,
 * so we take the user's recently-touched chapters from the progress
 * dashboard and fan out the per-chapter chat history query (the same
 * `GET /chat/history/{chapter_id}` the sheet uses) via React Query's
 * `useQueries`. We reuse the identical query key (`qk.chat.history`) and
 * shape as `useChatHistory`, so these queries share the cache with the
 * sheet -- opening a conversation here is instant.
 *
 * A card renders only for chapters with >=1 message. Tapping a card opens
 * the AI Tutor sheet pinned to that chapter (same handoff as the dashboard
 * mini-prompt), so the user drops straight back into the thread.
 */
export function ChatPage() {
  const dashboard = useDashboard();
  const me = useCurrentUser();

  const recent = useMemo<UserProgress[]>(
    () => dashboard.data?.recent_chapters ?? [],
    [dashboard.data],
  );

  // Subject names. Fall back to the first recent chapter's class when the
  // profile has no class_id yet (mirrors RecentChapters) so labels resolve.
  const effectiveClassId = me.data?.class_id ?? recent[0]?.class_id ?? null;
  const subjects = useSubjects(effectiveClassId ?? undefined);

  // Fan out one history query per recent chapter. Keyed + shaped exactly
  // like useChatHistory so the cache is shared with the sheet.
  const historyQueries = useQueries({
    queries: recent.map((p) => ({
      queryKey: qk.chat.history(p.chapter_id),
      staleTime: 30 * 1000,
      queryFn: async () => {
        const res = await api.get<ApiEnvelope<ChatMessage[]>>(
          `/chat/history/${encodeURIComponent(p.chapter_id)}`,
        );
        return unwrap(res.data) ?? [];
      },
    })),
  });

  const historyLoading = historyQueries.some((q) => q.isLoading);
  const isLoading = dashboard.isLoading || historyLoading;

  // Pull resolved histories into a stable array aligned to `recent`, and a
  // signature that only changes when some query's data changes -- so the
  // memo below doesn't rebuild on every render (useQueries returns a fresh
  // array reference each time).
  const histories = historyQueries.map((q) => q.data ?? []);
  const historySignature = historyQueries.map((q) => q.dataUpdatedAt).join(",");

  // Build conversation rows for chapters with at least one message, then
  // sort newest-first by the most recent message timestamp.
  const conversations = useMemo(() => {
    return recent
      .map((progress, i) => {
        const messages = histories[i] ?? [];
        if (messages.length === 0) return null;
        // History is oldest-first; the last entry is the latest message.
        const last = messages[messages.length - 1];
        return {
          progress,
          last,
          messageCount: messages.length,
          lastTimestamp: new Date(last.timestamp_iso).getTime(),
        };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)
      .sort((a, b) => b.lastTimestamp - a.lastTimestamp);
    // `histories` is a fresh array each render; `historySignature` captures
    // the meaningful change (a query resolving) without it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recent, historySignature]);

  return (
    <Container size="md" className="py-8 lg:py-12">
      <PageHeader
        title="AI Tutor"
        description="Your recent conversations. Pick up any thread right where you left it."
      />

      {isLoading ? (
        <ChatPageSkeleton />
      ) : conversations.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No conversations yet"
          body="Open any chapter and ask the AI Tutor a question — your conversations show up here so you can pick them back up anytime."
          action={
            <Link
              to="/subjects"
              className={cn(
                "inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm",
                "hover:opacity-95 transition-opacity",
              )}
            >
              <Sparkles className="h-4 w-4" />
              Browse subjects
            </Link>
          }
        />
      ) : (
        <StaggerList className="flex flex-col gap-3">
          {conversations.map((c) => (
            <StaggerItem key={c.progress.chapter_id}>
              <ConversationCard
                progress={c.progress}
                last={c.last}
                messageCount={c.messageCount}
                subjectName={
                  subjects.data?.find((s) => s.subject_id === c.progress.subject_id)
                    ?.subject_name ?? "Subject"
                }
              />
            </StaggerItem>
          ))}
        </StaggerList>
      )}
    </Container>
  );
}

function ConversationCard({
  progress,
  last,
  messageCount,
  subjectName,
}: {
  progress: UserProgress;
  last: ChatMessage;
  messageCount: number;
  subjectName: string;
}) {
  const openTutor = useAITutorStore((s) => s.open);
  const chaptersQ = useChapters(progress.subject_id);
  const chapterName =
    chaptersQ.data?.find((c) => c.chapter_id === progress.chapter_id)?.chapter_name ?? "Chapter";
  const Icon = getSubjectIcon(subjectName);

  const preview = previewText(last);
  const relative = relativeTime(last.timestamp_iso);

  return (
    <button
      type="button"
      onClick={() => openTutor({ chapterId: progress.chapter_id })}
      className={cn(
        "group w-full text-left",
        "rounded-2xl border border-border bg-surface p-4 sm:p-5",
        "hover:border-border-strong hover:-translate-y-px hover:shadow-[0_4px_12px_rgb(15_23_42_/_0.06)]",
        "dark:hover:bg-surface-2 dark:hover:shadow-none",
        "transition-[border-color,box-shadow,transform] duration-200 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <Bot className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] text-text-muted">
            <Icon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{subjectName}</span>
          </p>
          <h3 className="mt-0.5 truncate font-medium text-[15px] tracking-tight group-hover:text-brand transition-colors">
            {chapterName}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-text-muted leading-snug">
            {last.role === "assistant" ? (
              <span className="text-text-subtle">Tutor: </span>
            ) : (
              <span className="text-text-subtle">You: </span>
            )}
            {preview}
          </p>
          <div className="mt-2 flex items-center gap-2 text-[11px] text-text-subtle">
            <span>{relative}</span>
            <span aria-hidden>·</span>
            <span>
              {messageCount} message{messageCount === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-text-subtle group-hover:text-text-muted transition-colors" />
      </div>
    </button>
  );
}

function ChatPageSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-border bg-surface p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="mt-2 h-4 w-40" />
              <Skeleton className="mt-2.5 h-4 w-full" />
              <Skeleton className="mt-1.5 h-4 w-3/5" />
              <Skeleton className="mt-2.5 h-3 w-28" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Single-line preview of a message body. Strips markdown noise that would
 * read poorly in a one-liner (headings, list bullets, emphasis, code
 * fences) and collapses whitespace. Truncation itself is handled by the
 * line-clamp; this just keeps the text clean.
 */
function previewText(message: ChatMessage): string {
  return message.content
    .replace(/```[\s\S]*?```/g, " code ")
    .replace(/[#>*_`~-]+/g, " ")
    .replace(/\$\$?[^$]*\$\$?/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Relative timestamp via date-fns, guarding against bad/missing dates. */
function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return formatDistanceToNow(d, { addSuffix: true });
}
